import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { stripeAPI, pricingAPI, xenditAPI, adminSettingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  CreditCard,
  Loader2,
  CheckCircle,
  AlertTriangle,
  ArrowUpCircle,
  XCircle,
  RefreshCw,
  Tag,
  AlertCircle,
  X,
  FileText,
  Download,
  ExternalLink,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Default pricing fallback (used while loading)
const DEFAULT_PLANS = {
  single: { name: 'Single Location', monthly: 59, yearly: 348 },
  multi: { name: 'Multi Location', monthly: 119, yearly: 708 },
};

export default function Billing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [activeProcessor, setActiveProcessor] = useState('stripe');

  // Coupon state for upgrade flow
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponResult, setCouponResult] = useState(null);
  const [couponError, setCouponError] = useState('');

  useEffect(() => { document.title = 'Billing & Plans | ReStaff'; }, []);

  // Fetch pricing from backend (single source of truth)
  useEffect(() => {
    pricingAPI.getPricing()
      .then(({ data }) => {
        const backendPlans = {};
        Object.keys(data.plans).forEach((key) => {
          const p = data.plans[key];
          backendPlans[key] = {
            name: p.name,
            monthly: p.monthly_price,
            yearly: p.yearly_price,
          };
        });
        setPlans(backendPlans);
      })
      .catch(() => {
        // Keep default plans on error
      });
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, []);

  // Fetch invoice history for owner/admin
  useEffect(() => {
    if (user?.role === 'owner' || user?.role === 'admin') {
      setInvoicesLoading(true);
      stripeAPI.getInvoices()
        .then(({ data }) => setInvoices(data.invoices || []))
        .catch(() => {})
        .finally(() => setInvoicesLoading(false));
    }
  }, [user?.role]);

  const fetchSubscription = async () => {
    try {
      const res = await stripeAPI.getSubscriptionStatus();
      setSubscription(res.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to load subscription info');
      }
    } finally {
      setLoading(false);
    }
    // Load active processor (non-blocking)
    try {
      const { data } = await adminSettingsAPI.getPaymentConfig();
      setActiveProcessor(data.payment_processor || 'stripe');
    } catch (_) { /* non-critical — default to stripe */ }
  };

  // Coupon handlers
  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const { data } = await stripeAPI.validateCoupon({
        coupon_code: couponInput.trim(),
        plan_type: 'multi',
        billing_cycle: subscription?.billing_cycle || 'monthly',
      });
      setCouponResult(data);
    } catch (error) {
      setCouponError(error.response?.data?.detail || 'Invalid or expired promo code');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponResult(null);
    setCouponInput('');
    setCouponError('');
  };

  const handleUpgrade = async () => {
    // Trial user with no payment method — send to onboarding to set up billing first
    if (!subscription?.has_payment_method) {
      navigate('/onboarding');
      return;
    }
    setActionLoading(true);
    try {
      await stripeAPI.upgradePlan({ 
        new_plan_type: 'multi',
        ...(couponResult ? { coupon_code: couponResult.coupon_code } : {}),
      });
      toast.success('Plan upgraded to Multi Location!');
      removeCoupon();
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upgrade failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCycleChange = async (cycle) => {
    setActionLoading(true);
    try {
      await stripeAPI.changeBillingCycle(cycle);
      toast.success(`Switched to ${cycle} billing`);
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change billing cycle');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await stripeAPI.cancelSubscription();
      toast.success('Subscription will be canceled at end of billing period');
      setCancelDialogOpen(false);
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cancellation failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      await stripeAPI.resumeSubscription();
      toast.success('Subscription resumed!');
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Resume failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const { data } = await stripeAPI.createBillingPortalSession(
        window.location.origin + '/billing'
      );
      if (data.fallback || !data.url) {
        toast.info(
          'Xendit does not have a self-service portal. Contact support to update payment details or view invoices.',
          { duration: 6000 }
        );
        return;
      }
      window.location.href = data.url;
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 404 && detail === 'Not Found') {
        toast.error('Billing portal is not available. Please contact support.');
      } else {
        toast.error(detail || 'Could not open billing portal');
      }
    } finally {
      setPortalLoading(false);
    }
  };

  const handleSubscribe = async (planType) => {
    setActionLoading(true);
    try {
      if (activeProcessor === 'xendit') {
        const planData = plans[planType] || DEFAULT_PLANS[planType];
        const amount = planData?.monthly || planData?.monthly_price || 59;
        const res = await xenditAPI.createInvoice({
          plan_type: planType,
          billing_cycle: 'monthly',
          amount,
          description: `ReStaff ${planData?.name || planType} Subscription`,
        });
        if (res.data?.invoice_url) {
          window.location.href = res.data.invoice_url;
        }
      } else {
        const res = await stripeAPI.createCheckout({
          plan_type: planType,
          billing_cycle: 'monthly',
        });
        if (res.data?.checkout_url) {
          window.location.href = res.data.checkout_url;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      active: { label: 'Active', className: 'bg-green-100 text-green-700' },
      trial: { label: 'Free Trial', className: 'bg-blue-100 text-blue-700' },
      past_due: { label: 'Past Due', className: 'bg-red-100 text-red-700' },
      canceled: { label: 'Canceled', className: 'bg-slate-100 text-slate-700' },
      free: { label: 'Free', className: 'bg-slate-100 text-slate-600' },
    };
    const s = map[status] || map.free;
    return <Badge className={s.className} data-testid="subscription-status-badge">{s.label}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  const plan = subscription ? plans[subscription.plan_type] : null;
  const price = plan
    ? subscription.billing_cycle === 'yearly' ? plan.yearly : plan.monthly
    : 0;
  const isActive = subscription?.status === 'active' || subscription?.subscription_status === 'active';
  const isTrial = subscription?.status === 'trial';
  const isCanceling = subscription?.subscription_status === 'canceled' || subscription?.cancel_at_period_end;

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Billing & Subscription
          </h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base">Manage your plan and billing</p>
        </div>

        {/* Current Plan */}
        <Card className="border-slate-200" data-testid="current-plan-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard size={20} className="text-orange-500" />
                Current Plan
              </CardTitle>
              {subscription && getStatusBadge(subscription.status)}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {subscription && plan ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Plan</p>
                    <p className="font-semibold text-slate-900" data-testid="plan-name">{plan.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Price</p>
                    <p className="font-semibold text-slate-900" data-testid="plan-price">
                      ${price}/{subscription.billing_cycle === 'yearly' ? 'yr' : 'mo'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Billing Cycle</p>
                    <p className="font-semibold text-slate-900 capitalize">{subscription.billing_cycle}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Next Billing</p>
                    <p className="font-semibold text-slate-900">
                      {subscription.current_period_end
                        ? new Date(subscription.current_period_end).toLocaleDateString()
                        : isTrial && subscription.trial_ends_at
                          ? new Date(subscription.trial_ends_at).toLocaleDateString()
                          : '—'}
                    </p>
                  </div>
                </div>

                {isTrial && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                    <AlertTriangle size={18} className="text-blue-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Free Trial</p>
                      <p className="text-xs text-blue-600">
                        Trial ends {subscription.trial_ends_at
                          ? new Date(subscription.trial_ends_at).toLocaleDateString()
                          : 'soon'}. Subscribe to keep your data.
                      </p>
                    </div>
                  </div>
                )}

                {/* Applied Coupon / Discount */}
                {subscription?.applied_discount && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3" data-testid="billing-applied-discount">
                    <Tag size={18} className="text-green-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Active Discount
                      </p>
                      <p className="text-xs text-green-600">
                        {subscription.applied_discount.percent_off
                          ? `${subscription.applied_discount.percent_off}% off`
                          : subscription.applied_discount.amount_off
                            ? `$${subscription.applied_discount.amount_off} off`
                            : 'Discount applied'}
                        {subscription.coupon_code_used && ` (${subscription.coupon_code_used})`}
                        {subscription.applied_discount.duration === 'forever' && ' — Lifetime'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Next Billing Amount */}
                {subscription?.next_billing_amount != null && (isActive && !isCanceling) && (
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="billing-next-amount">
                    <p className="text-xs text-slate-500 mb-0.5">Next Billing Amount</p>
                    <p className="text-lg font-semibold text-slate-900">
                      ${subscription.next_billing_amount.toFixed(2)}
                      <span className="text-xs font-normal text-slate-500 ml-1">
                        on {subscription.current_period_end
                          ? new Date(subscription.current_period_end).toLocaleDateString()
                          : '—'}
                      </span>
                    </p>
                  </div>
                )}

                {isCanceling && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-start gap-3">
                      <XCircle size={18} className="text-amber-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Cancellation Scheduled</p>
                        <p className="text-xs text-amber-600">Your subscription will end at the current billing period.</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={handleResume}
                      disabled={actionLoading}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="resume-subscription-btn"
                    >
                      {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw size={14} className="mr-1" /> Resume</>}
                    </Button>
                  </div>
                )}

                {/* Manage Billing Portal — owner/admin only */}
                {(user?.role === 'owner' || user?.role === 'admin') && (
                  <div className="pt-2 border-t border-slate-100">
                    <Button
                      variant="outline"
                      onClick={handleManageBilling}
                      disabled={portalLoading}
                      className="w-full min-h-[44px] flex items-center justify-center gap-2 text-slate-700 border-slate-300 hover:bg-slate-50"
                      data-testid="manage-billing-portal-btn"
                    >
                      {portalLoading
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <><CreditCard size={16} className="text-slate-500" /> Manage Billing Portal</>}
                    </Button>
                    <p className="text-xs text-slate-400 text-center mt-1.5">
                      Update payment method, view invoices or cancel
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-slate-500">No active subscription</p>
                <p className="text-sm text-slate-400 mt-1">Choose a plan below to get started</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Upgrade */}
          {subscription?.plan_type === 'single' && (isActive || isTrial) && (
            <Card className="border-slate-200 sm:col-span-2">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <ArrowUpCircle size={20} className="text-orange-500" />
                  <h3 className="font-semibold text-slate-900">Upgrade Plan</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">Switch to Multi Location for unlimited locations.</p>

                {/* Coupon Code Section */}
                <div className="mb-4" data-testid="upgrade-coupon-section">
                  {!showCoupon ? (
                    <button
                      type="button"
                      onClick={() => setShowCoupon(true)}
                      className="flex items-center gap-1.5 text-sm text-orange-500 hover:text-orange-600 font-medium transition-colors"
                      data-testid="show-upgrade-coupon-btn"
                    >
                      <Tag size={13} />
                      Have a promo code?
                    </button>
                  ) : couponResult ? (
                    <div
                      className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                      data-testid="upgrade-coupon-applied"
                    >
                      <div className="flex items-center gap-2">
                        <CheckCircle size={15} className="text-green-600 flex-shrink-0" />
                        <span className="text-sm font-medium text-green-800">
                          {couponResult.coupon_code} &mdash; <strong>{couponResult.discount_display}</strong>
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={removeCoupon}
                        data-testid="remove-upgrade-coupon-btn"
                        className="text-slate-400 hover:text-red-500 transition-colors ml-2 flex-shrink-0"
                      >
                        <X size={15} />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Promo Code (optional)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="Enter promo code"
                          value={couponInput}
                          onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleApplyCoupon())}
                          className="flex-1 uppercase tracking-wide placeholder:normal-case placeholder:tracking-normal"
                          data-testid="upgrade-coupon-input"
                          maxLength={50}
                          autoComplete="off"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleApplyCoupon}
                          disabled={couponLoading || !couponInput.trim()}
                          data-testid="apply-upgrade-coupon-btn"
                          className="shrink-0 px-4"
                        >
                          {couponLoading ? <Loader2 size={15} className="animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                      {couponError && (
                        <p className="text-xs text-red-600 flex items-center gap-1" data-testid="upgrade-coupon-error">
                          <AlertCircle size={12} className="flex-shrink-0" />
                          {couponError}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleUpgrade}
                  disabled={actionLoading}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  data-testid="upgrade-plan-btn"
                >
                  {actionLoading
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : !subscription?.has_payment_method
                      ? 'Add Payment Method to Upgrade'
                      : couponResult
                        ? `Upgrade to Multi (${couponResult.discount_display})`
                        : `Upgrade to Multi ($${plans.multi?.monthly || 119}/mo)`}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Change Billing Cycle */}
          {(isActive || isTrial) && subscription?.stripe_subscription_id && (
            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <CreditCard size={20} className="text-slate-600" />
                  <h3 className="font-semibold text-slate-900">Billing Cycle</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  Currently: <strong className="capitalize">{subscription.billing_cycle}</strong>
                </p>
                <Button
                  onClick={() => handleCycleChange(subscription.billing_cycle === 'monthly' ? 'yearly' : 'monthly')}
                  disabled={actionLoading}
                  variant="outline"
                  className="w-full"
                  data-testid="change-cycle-btn"
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> :
                    `Switch to ${subscription.billing_cycle === 'monthly' ? 'Yearly (save 2 months)' : 'Monthly'}`}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Subscribe buttons for no-subscription users */}
          {(!subscription || subscription.status === 'free') && (
            <>
              <Card className="border-orange-200 bg-orange-50/30">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-900 mb-1">Single Location</h3>
                  <p className="text-2xl font-bold text-orange-600">${plans.single?.monthly || 59}<span className="text-sm font-normal text-slate-500">/mo</span></p>
                  <p className="text-sm text-slate-500 mt-1 mb-4">1 location, 30-day free trial</p>
                  <Button onClick={() => handleSubscribe('single')} disabled={actionLoading} className="w-full bg-orange-500 hover:bg-orange-600" data-testid="subscribe-single-btn">
                    {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Free Trial'}
                  </Button>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-900 mb-1">Multi Location</h3>
                  <p className="text-2xl font-bold text-slate-800">${plans.multi?.monthly || 119}<span className="text-sm font-normal text-slate-500">/mo</span></p>
                  <p className="text-sm text-slate-500 mt-1 mb-4">Unlimited locations, 30-day free trial</p>
                  <Button onClick={() => handleSubscribe('multi')} disabled={actionLoading} variant="outline" className="w-full" data-testid="subscribe-multi-btn">
                    {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Free Trial'}
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Cancel subscription */}
        {(isActive || isTrial) && subscription?.stripe_subscription_id && !isCanceling && (
          <Card className="border-slate-200">
            <CardContent className="p-5 flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="font-semibold text-slate-700">Cancel Subscription</h3>
                <p className="text-sm text-slate-500">Your data will remain until the end of the billing period.</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setCancelDialogOpen(true)}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="cancel-subscription-btn"
              >
                Cancel Plan
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Billing History — owner/admin only */}
        {(user?.role === 'owner' || user?.role === 'admin') && (
          <Card className="border-slate-200" data-testid="billing-history-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText size={20} className="text-slate-500" />
                Billing History
              </CardTitle>
              <CardDescription>Last 10 invoices from Stripe</CardDescription>
            </CardHeader>
            <CardContent>
              {invoicesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-8" data-testid="billing-history-empty">
                  <FileText size={32} className="text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">No invoices yet</p>
                  <p className="text-slate-400 text-xs mt-0.5">
                    Invoices will appear here once your subscription is active
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100" data-testid="billing-history-list">
                  {invoices.map((inv) => (
                    <div
                      key={inv.id}
                      className="flex items-center justify-between py-3 gap-3 flex-wrap"
                      data-testid={`invoice-row-${inv.id}`}
                    >
                      {/* Date + Description */}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {inv.description || 'Subscription Invoice'}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {inv.date
                            ? new Date(inv.date).toLocaleDateString(undefined, {
                                year: 'numeric', month: 'short', day: 'numeric',
                              })
                            : '—'}
                        </p>
                      </div>

                      {/* Amount + Status */}
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-900">
                            ${inv.amount_paid.toFixed(2)}
                            <span className="text-xs font-normal text-slate-400 ml-0.5">
                              {inv.currency}
                            </span>
                          </p>
                        </div>
                        <Badge
                          className={
                            inv.status === 'paid'
                              ? 'bg-green-100 text-green-700 text-xs'
                              : inv.status === 'open'
                              ? 'bg-amber-100 text-amber-700 text-xs'
                              : inv.status === 'void'
                              ? 'bg-slate-100 text-slate-500 text-xs'
                              : 'bg-red-100 text-red-700 text-xs'
                          }
                          data-testid={`invoice-status-${inv.id}`}
                        >
                          {inv.status === 'paid' ? 'Paid' :
                           inv.status === 'open' ? 'Due' :
                           inv.status === 'void' ? 'Void' : inv.status}
                        </Badge>

                        {/* Download PDF / View */}
                        {inv.invoice_pdf ? (
                          <a
                            href={inv.invoice_pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-400 hover:text-orange-500 transition-colors"
                            title="Download PDF"
                            data-testid={`invoice-pdf-${inv.id}`}
                          >
                            <Download size={16} />
                          </a>
                        ) : inv.hosted_invoice_url ? (
                          <a
                            href={inv.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-400 hover:text-orange-500 transition-colors"
                            title="View Invoice"
                            data-testid={`invoice-view-${inv.id}`}
                          >
                            <ExternalLink size={16} />
                          </a>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Cancel confirmation */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <AlertTriangle size={20} />
                Cancel Subscription
              </DialogTitle>
              <DialogDescription>Are you sure you want to cancel?</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  Your subscription will remain active until the end of the current billing period.
                  After that, you will lose access to premium features.
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setCancelDialogOpen(false)}>
                  Keep Plan
                </Button>
                <Button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-cancel-btn"
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Cancellation'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
